import styled, { css } from 'styled-components';

import FilterCard from '@spa/shared/components/FilterCard';
import { TooltipContainer } from '@spa/shared/components/Tooltip/styles';

const cellSpaces = 10;

export const Container = styled.div`
  width: 100%;
  position: relative;
  justify-content: flex-start;
  display: flex;
  flex: 1;
  user-select: none;
`;

export const InputContainer = styled.label`
  ${FilterCard}

  flex: 1;
  min-width: 50px;
  cursor: pointer;

  &.disabled {
    background: transparent;
    opacity: 0.5;
    pointer-events: none;
  }

  svg {
    width: 20px;
    margin-right: ${props => props.theme.space(2)};
    pointer-events: none;
  }

  &:first-of-type {
    margin-right: ${props => props.theme.space(2)};
  }
`;

export const Input = styled.input`
  border: 0;
  font-size: ${props => props.theme.rem(14)};
  font-family: IntervalNextReg, sans-serif;
  font-weight: 100;
  line-height: normal;
  color: ${props => props.theme.dateRange.color};
  background: transparent;
  border: 0;
  min-width: 50px;
  cursor: pointer;
`;

export const DayPickerContainer = styled.div`
  ${FilterCard}

  box-shadow: ${props => props.theme.dateRange.pickerBoxShadow};
  height: auto;
  flex-direction: column;
  width: 100%;
  min-width: ${props => props.theme.space(cellSpaces + 2)};
  justify-content: center;
  padding: ${props => props.theme.space(6)};
`;

export const MonthContainer = styled.table`
  background: white;
  width: 100%;

  & > tbody {
    display: flex;
    flex-direction: column;
    justify-content: left;
    ${({ theme }) => theme.gap(1, 'column')};
  }
`;

const ClassNameStyles = css`
  &.available-move-in {
    color: ${props => props.theme.unisystem.calendar.selected.background};
    border: 1px solid ${props => props.theme.unisystem.calendar.selected.background};
    border-radius: 56px;
  }

  &.selected-between {
    background-color: ${props => props.theme.unisystem.calendar.selectedBetweenBackground};
    border: none;
    color: ${props => props.theme.unisystem.calendar.selected.between.color};

    &.first {
      border-top-left-radius: 56px;
      border-bottom-left-radius: 56px;
    }

    &.last {
      border-top-right-radius: 56px;
      border-bottom-right-radius: 56px;
    }
  }

  &.minimum-stay-last {
    &:not(.selected-between) {
      z-index: 0;
      position: relative;
      color: ${props => props.theme.unisystem.calendar.selected.color};

      &:before {
        content: '';
        position: absolute;
        background: ${props => props.theme.unisystem.calendar.selectedBetweenBackground};
        height: 100%;
        z-index: -2;
        width: 50%;
        left: 0;
      }

      &:after {
        content: '';
        position: absolute;
        background: ${props => props.theme.unisystem.calendar.hoverBackground};
        border-radius: 56px;
        width: 40px;
        height: 40px;
        z-index: -1;
      }
    }
  }

  &.disabled {
    color: ${props => props.theme.unisystem.calendar.disabledDatesColor};
    position: relative;
    pointer-events: none;
    cursor: not-allowed;

    &.minimum-stay {
      background-color: ${props => props.theme.unisystem.calendar.selectedBetweenBackground};
      border: none;

      &.first {
        border-top-left-radius: 56px;
        border-bottom-left-radius: 56px;
      }

      &.last {
        border-top-right-radius: 56px;
        border-bottom-right-radius: 56px;
      }
    }

    &:not(.no-day):after {
      content: '';
      width: 25px;
      height: 1px;
      background: ${props => props.theme.unisystem.calendar.disabledDatesColor};
      position: absolute;
      border-radius: 56px;
    }

    &.minimum-stay,
    &.max-move-in {
      pointer-events: auto;
    }

    &.minimum-stay,
    &.selected {
      color: ${props => props.theme.unisystem.calendar.selected.between.color};

      &:after {
        content: none;
      }
    }
  }

  &.first {
    &:before {
      border-top-left-radius: 56px;
      border-bottom-left-radius: 56px;
    }
  }

  &:last-of-type {
    ${TooltipContainer} {
      white-space: normal;
      left: -40px;

      &:after {
        left: 84%;
      }
    }
  }

  &:first-of-type {
    ${TooltipContainer} {
      white-space: normal;
      left: 55px;

      &:after {
        left: 16%;
      }
    }
  }
`;

export const DayContainer = styled.th`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: ${props => props.theme.rem(14)};
  width: ${props => (props.choosingStart ? '40px' : '100%')};
  height: 40px;
  height: ${props => props.theme.space(cellSpaces)};
  font-family: IntervalNextReg, sans-serif;
  font-weight: 100;
  line-height: normal;
  color: ${props => props.theme.unisystem.calendar.color};

  ${ClassNameStyles}

  ${TooltipContainer} {
    min-width: 100%;
    white-space: nowrap;

    &:after {
      top: 98%;
    }
  }

  ${TooltipContainer} {
    min-width: 100%;
    width: 140px;
    white-space: normal;

    &:after {
      top: 98%;
    }
  }
`;

export const Arrow = styled.span`
  display: flex;

  svg {
    width: 13px;
    transform: rotateZ(0deg);
    transition: transform 0.25s ease-in-out;
    margin-right: auto;
  }

  &.down {
    svg {
      transform: rotateZ(180deg);
    }
  }
`;

export const RowContainer = styled.tr`
  display: inline-flex;
  align-content: center;
  justify-content: space-between;
  height: 40px;
`;

export const WeekContainer = styled.ul`
  display: inline-flex;
  margin-top: ${props => props.theme.space(8)};
  margin-bottom: ${props => props.theme.space(2)};
  width: 100%;
  min-height: min-content;
  justify-content: space-between;
`;

export const WeekDayContainer = styled.li`
  width: ${100 / 7}%;
  color: ${props => props.theme.dateRange.color};
  font-size: ${props => props.theme.rem(14)};
  font-family: IntervalNextReg, sans-serif;
  font-weight: 100;
  line-height: ${props => props.theme.rem(18)};
  text-align: center;
  text-transform: uppercase;
`;

export const MonthSelector = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.gap(1, 'column')};
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 ${props => props.theme.space(3)};
  margin-bottom: 0;
  min-height: min-content;
`;

export const MonthLabel = styled.div`
  font-size: ${props => props.theme.rem(16)};
  line-height: ${props => props.theme.rem(20)};
  font-family: IntervalNextReg, sans-serif;
  color: ${props => props.theme.unisystem.calendar.color};
  text-align: center;
  width: 100%;
  text-transform: capitalize;
`;

export const MonthPrice = styled.div`
  font-size: ${props => props.theme.rem(14)};
  line-height: ${props => props.theme.rem(18)};
  font-family: IntervalNextReg, sans-serif;
  color: ${props => props.theme.dateRange.color};
  text-align: center;
  width: 100%;
`;

const MonthSelectorArrow = styled.div`
  color: ${props => props.theme.dateRange.color};
  cursor: pointer;
  display: flex;
  border-radius: 2px;
  border: ${props => `1px solid ${props.theme.dateRange.arrowBordersColor}`};
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  svg {
    width: 13px;
    height: 13px;
    display: block;
  }
`;

export const MonthSelectorLeftArrow = styled(MonthSelectorArrow)`
  transform: rotateZ(-90deg);
`;

export const MonthSelectorRightArrow = styled(MonthSelectorArrow)`
  transform: rotateZ(90deg);
`;

const SelectedClassNameStyles = css`
  .selected {
    background: transparent;
    position: relative;
    color: ${props => props.theme.unisystem.calendar.selected.color};
    z-index: 0;

    &.selected-start {
      color: ${props => props.theme.unisystem.calendar.selected.color};

      &:before {
        left: auto;
        right: 0;
      }

      &:after {
        content: '';
        border-top-right-radius: 56px;
        border-bottom-right-radius: 56px;
        background-color: ${props => props.theme.unisystem.calendar.selected.background};
        height: 40px;
        width: 40px;
      }
    }

    &.selected-end {
      &:after {
        background: ${props => props.theme.unisystem.calendar.selected.background};
      }
    }

    &:before {
      content: '';
      position: absolute;
      left: 0;
      width: 50%;
      height: 40px;
      right: 0;
      background-color: ${props => props.theme.unisystem.calendar.selectedBetweenBackground};
      z-index: -2;
    }

    &:after {
      content: '';
      position: absolute;
      width: 40px;
      height: 40px;
      background-color: ${props => props.theme.unisystem.calendar.selected.background};
      border-radius: 56px;
      z-index: -1;
    }
  }
`;

export const TableRangeMonths = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme }) => theme.gap(3, 'column')};
  min-height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-bottom: ${props => props.theme.space(12)};

  button {
    flex-shrink: 0;
    align-self: center;
    width: 95%;

    &:first-of-type {
      margin-top: ${props => props.theme.space(3)};
      margin-right: ${props => props.theme.space(2)};
      margin-left: ${props => props.theme.space(2)};
    }

    &:last-of-type {
      margin-bottom: ${props => props.theme.space(16)};
    }
  }

  ${SelectedClassNameStyles}

  tbody {
    row-gap: ${props => props.theme.space(2)};
  }
`;

export const TableRangeMonth = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  flex: 1;
  min-height: min-content;

  table,
  ul {
    width: 100%;
  }
`;
